import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { version } from '../../package.json';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function fetchWithVersion(url: string | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers ?? {});
  headers.set('X-Request-Version', version);
  headers.set('X-Request-Client', 'MYCOACH Chat');
  if (init.body) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, {
    ...init,
    headers,
  });
}

export default function negotiateAsset(asset: any, size = 1600, thumb = false) {
  if (!asset) {
    return asset;
  }

  if (typeof asset === 'string') {
    return asset;
  }

  // assets neue
  if ('file' in asset) {
    if (thumb) {
      if (size) {
        const f = asset.thumbnails.find((t: any) => t.dimension === size);
        if (f) {
          return f.url;
        }
        if ('getImageThumbUrl' in asset) {
          return asset.getImageThumbUrl(size);
        }
        if (asset._links) {
          return asset._links['ec:dm-asset/thumbnail'][0].href.replace('{size}', size);
        }
      }
      return asset.thumbnails[0].url;
    }
    if (size) {
      if (asset.mimetype === 'image/svg+xml') {
        return asset.file.url;
      }

      if (!size || Math.max(asset.file.resolution.width, asset.file.resolution.height) <= size) {
        return asset.file.url;
      }

      const f = asset.fileVariants.find((v: any) => Math.max(v.resolution.width, v.resolution.height) === size);
      if (f) {
        return f.url;
      }
      if ('getImageUrl' in asset) {
        return asset.getImageUrl(size);
      }
      if (asset._links) {
        return asset._links['ec:dm-asset/file-variant'][0].href.replace('{size}', size);
      }
    }
    return asset.file.url;
  }

  // legacy asset logic
  if ('files' in asset) {
    let f = JSON.parse(JSON.stringify(asset.files));

    const first = f[0];
    // remove image files we have no resolution for (image/svg+xml; fix for CMS-1091)
    f = f.filter((file: any) => file.resolution);
    if (f.length === 0) {
      // if no file is left pick first of original data
      return first.url;
    }
    f.sort((left: any, right: any) => {
      // sort by size descending
      const leftMax = Math.max(left.resolution.height, left.resolution.width);
      const rightMax = Math.max(right.resolution.height, right.resolution.width);
      if (leftMax < rightMax) {
        return 1;
      }
      if (leftMax > rightMax) {
        return -1;
      }
      return 0;
    });
    let imageFiles = f.filter((file: any) => {
      if (thumb) {
        return file.url.indexOf('_thumb') !== -1; // is thumbnail
      }
      return file.url.indexOf('_thumb') === -1; // is not a thumbnail
    });
    if (!imageFiles || imageFiles.length === 0) {
      imageFiles = f;
    }
    const largest = imageFiles[0];
    if (size) {
      // remove all image resolutions that are too small
      imageFiles = imageFiles
        .filter((file: any) => file.resolution.height >= size || file.resolution.width >= size)
        // choose smallest image of all that are greater than size parameter
        .slice(-1);
    }

    if (imageFiles.length > 0) {
      // if all is good, we have an image now
      return imageFiles[0].url;
    }
    // if the requested size is larger than the original image, we take the largest possible one
    return largest.url;
  }
}
